import { useMemo } from "react";

import PlusIcon from "@uui/Plus";
import { isEmpty, toLower } from "lodash/fp";

import Button, { ButtonProps } from "@mui/material/Button";
import styled from "@mui/material/styles/styled";

import EditableContent from "@app/analytics/MeetingReport/components/EditableContent";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import type {
  NoteItem,
  NoteSearchResult,
  SessionNoteEdit,
  SummaryItemEdit,
} from "@app/analytics/MeetingReport/types";
import {
  chunkNoteBySearchResults,
  isNote,
} from "@app/analytics/MeetingReport/utils";
import {
  getTemporaryId,
  isNewItem,
} from "@app/analytics/MeetingReport/utils/editUtils";
import type { NoteType, TranscriptClipType, User } from "@app/common/types";
import { titleize } from "@app/utils/utils";

import { Column } from "@rui/Stack";
import { Span } from "@rui/styles";

const EMPTY_SEARCH_RESULTS: NoteSearchResult[] = [];

interface EditableNoteSubSectionProps {
  canCreateNew: boolean;
  /* a custom list of items to display when editing. if undefined, will pull
  all items of the defined type straight from the edit store */
  items?: NoteItem[];
  type: NoteType;
  searchResults: Map<string, NoteSearchResult[]>;
  sessionId: string;
  userId: User["id"];
}

const NewItemButton = styled((props: ButtonProps) => (
  <Button {...props} startIcon={<PlusIcon />} />
))(({ theme }) => ({
  ...theme.typography.bodyPrimaryMed,
  width: "fit-content",
  fontFamily: theme.typography.fontFamilySecondary,
  marginLeft: theme.spacing(1),
  "& .MuiButton-startIcon svg": {
    fontSize: "16px",
  },
}));

const Bullet = styled((props) => <Span {...props}>•</Span>)(({ theme }) => ({
  marginRight: theme.spaces.small,
}));

const EditableNote = ({
  isEditing,
  item,
  searchResults,
  type,
  ...contentProps
}: {
  isEditing: boolean;
  item: NoteItem;
  onDelete: () => void;
  onDiscard: () => void;
  onSave: (updatedValue: string) => void;
  onToggleEditing: (editing: boolean) => void;
  searchResults: NoteSearchResult[];
  type: EditableNoteSubSectionProps["type"];
}) => {
  if (item.isDeleted) return null;

  const contentChunks = useMemo(() => {
    if (searchResults.length) {
      return chunkNoteBySearchResults(item.value, searchResults);
    }
    return item.value;
  }, [item.value, searchResults]);

  const noteType = titleize(type);

  return (
    <EditableContent
      {...contentProps}
      key={item.id}
      prefix={<Bullet />}
      editing={isEditing}
      value={item.value}
      type={toLower(noteType)}
      placeholder={isNewItem(item) ? `New ${noteType}` : undefined}
      contentChunks={contentChunks}
    />
  );
};

const EditableNoteSubSection = ({
  canCreateNew,
  items,
  type,
  searchResults,
  sessionId,
  userId,
}: EditableNoteSubSectionProps) => {
  const { activeItem, editedItems, onStartEdit, onCloseEdit, saveEdit } =
    useEditStore((state) => ({
      activeItem: state.notes.activeItem,
      editedItems: items ?? (state.getItemsByType(type) as NoteItem[]),
      onStartEdit: state.startEditingItem,
      onCloseEdit: state.exitItem,
      saveEdit: state.save,
    }));

  const handleDeleteItem = (item: NoteItem) => () => {
    if (isNote(item)) {
      const edit: SessionNoteEdit = {
        type: "note",
        before: item,
        after: { ...item, isDeleted: true },
      };
      saveEdit(edit);
      return;
    }

    const edit: SummaryItemEdit = {
      type: type as TranscriptClipType,
      before: item,
      after: { ...item, isDeleted: true },
    };
    saveEdit(edit);
  };

  const handleSaveEdits = (item: NoteItem) => (updatedValue: string) => {
    if (isEmpty(updatedValue)) {
      // treat any item saved w/o text as a delete
      return handleDeleteItem(item)();
    }

    if (updatedValue === item.value) return;

    if (isNote(item)) {
      const edit: SessionNoteEdit = {
        type: "note",
        before: item,
        after: { ...item, value: updatedValue },
      };
      saveEdit(edit);
      return;
    }

    const edit: SummaryItemEdit = {
      type: type as TranscriptClipType,
      before: item,
      after: { ...item, value: updatedValue },
    };
    saveEdit(edit);
  };

  const handleCreateItem = () => {
    if (type === "note") {
      const edit: SessionNoteEdit = {
        type: "note",
        after: {
          id: getTemporaryId(),
          value: "",
          sessionId,
          userId,
          isEdited: true,
          isDeleted: false,
        },
      };
      saveEdit(edit);
      return;
    }

    const edit: SummaryItemEdit = {
      type,
      after: {
        id: getTemporaryId(),
        value: "",
        type,
        isEdited: true,
        isDeleted: false,
      },
    };
    saveEdit(edit);
  };

  const handleToggleEditing = (item: NoteItem) => (editing: boolean) => {
    if (editing) {
      onStartEdit({ type: type, item });
    } else {
      onCloseEdit();
    }
  };

  const handleDiscard = (item: NoteItem) => () => {
    if (!item.value) {
      handleDeleteItem(item)();
    }
  };

  return (
    <Column>
      {editedItems.map((item) => (
        <EditableNote
          key={item.id}
          isEditing={activeItem?.type === type && activeItem?.id === item?.id}
          item={item}
          onDelete={handleDeleteItem(item)}
          onDiscard={handleDiscard(item)}
          onSave={handleSaveEdits(item)}
          onToggleEditing={handleToggleEditing(item)}
          searchResults={searchResults.get(item.id) ?? EMPTY_SEARCH_RESULTS}
          type={type}
        />
      ))}
      {canCreateNew && (
        <NewItemButton onClick={handleCreateItem}>
          Add {titleize(type)}
        </NewItemButton>
      )}
    </Column>
  );
};

export default EditableNoteSubSection;
